import * as React from "react";
import { useState } from "react";
import PropTypes from 'prop-types';
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';

const CustomNoRowsOverlay = () => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        height: 285,
        width: "100%",
        background: '#fff',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1,
        }}
      >
        <Typography sx={{ color: '#666', fontSize: '2rem' }}>
          📊
        </Typography>
      </Box>
      <Typography 
        variant="body1" 
        sx={{ 
          color: "#666", 
          fontFamily: 'Poppins',
          fontWeight: 500,
          textAlign: 'center',
          fontSize: '14px'
        }}
      >
        No data found
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: "#999", 
          fontFamily: 'Poppins',
          fontSize: '13px',
          textAlign: 'center'
        }}
      >
        Data will appear here once available
      </Typography>
    </Box>
  );
};

const TableComponent = ({ 
  rows, 
  columns, 
  getRowHeight, 
  filterText = "",
  pageSize = 10,
  searchPlaceholder = "Search...",
  actionButtons = null,
}) => {
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  
  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) =>
        typeof value === "string" &&
        value.toLowerCase().includes(filterText.toLowerCase())
    )
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Search and Actions Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '0 16px'
      }}>
        <TextField
          placeholder={searchPlaceholder}
          size="small"
          value={filterText}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '36px',
              borderRadius: '4px',
              '& fieldset': {
                borderColor: '#E0E0E0'
              },
              '&:hover fieldset': {
                borderColor: '#9AA6AC'
              }
            },
            '& .MuiOutlinedInput-input': {
              fontSize: '14px',
              fontFamily: 'Poppins',
              padding: '8px 14px'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9AA6AC', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        {actionButtons}
      </Box>

      <DataGrid
        rows={filteredRows.map((row, index) => ({ ...row, id: index + 1 }))}
        columns={columns}
        pageSize={pageSize}
        rowsPerPageOptions={[10, 25, 50]}
        pagination
        page={page}
        onPageChange={(newPage) => setPage(newPage)}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        disableSelectionOnClick
        getRowId={(row) => row.id}
        getRowHeight={getRowHeight}
        components={{
          NoRowsOverlay: CustomNoRowsOverlay
        }}
        sx={{
          border: 'none',
          backgroundColor: '#F4F4F4',
          width: '100%',
          height: 'calc(100vh - 250px)',
          '& .MuiDataGrid-columnHeaders': {
            fontFamily: 'Poppins',
            fontSize: '14px',
            fontWeight: 600,
            color: '#666',
            borderBottom: '1px solid #E0E0E0',
            padding: '12px 16px',
            minHeight: '48px !important',
            backgroundColor: '#fff',
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            fontSize: '0.8rem',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
          },
          "& .MuiDataGrid-cell": {
            fontFamily: 'Poppins',
            fontSize: '13px',
            color: '#333',
            borderBottom: '1px solid #E0E0E0',
            padding: '12px 16px',
            backgroundColor: '#fff',
          },
          "& .MuiDataGrid-row": {
            minHeight: '48px !important',
            '&:hover': {
              backgroundColor: '#F9FAFB',
            }
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: '48px',
            borderTop: '1px solid #E0E0E0',
            backgroundColor: '#fff',
            padding: '0 16px',
          },
          "& .MuiTablePagination-root": {
            fontFamily: 'Poppins',
            '& .MuiTablePagination-select': {
              fontFamily: 'Poppins',
            }
          },
          "& .MuiTablePagination-displayedRows": {
            fontFamily: 'Poppins',
            fontSize: '13px',
            color: '#666',
          },
          "& .MuiIconButton-root": {
            color: '#666',
            '&:hover': {
              backgroundColor: '#F4F4F4',
            },
            '&.Mui-disabled': {
              color: '#E0E0E0',
            }
          }
        }}
      />
    </Box>
  );
};

TableComponent.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  getRowHeight: PropTypes.func,
  filterText: PropTypes.string,
  pageSize: PropTypes.number,
  searchPlaceholder: PropTypes.string,
  actionButtons: PropTypes.node
};

export default TableComponent;